"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { STDIN_MAX_LENGTH } from "../types";
import { loadStdinExpanded, saveStdinExpanded } from "../lib/playground-stdin-storage";

/** Show the character counter once input is large enough for the cap to matter. */
const COUNTER_VISIBLE_FROM = Math.floor(STDIN_MAX_LENGTH * 0.8);

type PlaygroundStdinPanelProps = {
  value: string;
  onChange: (value: string) => void;
  fontSize: number;
  /**
   * `RV-13`: open or folded, when the caller owns it. The Learn pane opens the
   * panel on a lesson input and keeps its own state, so it neither reads nor
   * writes the Playground's stored preference. Unset, the panel keeps that
   * preference as before.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

/**
 * Stdin fills the bottom pane of the output column (splitter layout).
 * Collapsible — docs/24_PLAYGROUND_SPEC.md §10.5.
 */
export function PlaygroundStdinPanel({
  value,
  onChange,
  fontSize,
  expanded: controlledExpanded,
  onExpandedChange,
}: PlaygroundStdinPanelProps) {
  const t = useTranslations("playground");
  const controlled = controlledExpanded !== undefined;
  const [ownExpanded, setOwnExpanded] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const expanded = controlled ? controlledExpanded : ownExpanded;
  const setExpanded = (update: (current: boolean) => boolean) => {
    if (controlled) onExpandedChange?.(update(controlledExpanded));
    else setOwnExpanded(update);
  };

  useEffect(() => {
    if (controlled) return;
    setOwnExpanded(loadStdinExpanded());
    setHydrated(true);
  }, [controlled]);

  useEffect(() => {
    if (!hydrated || controlled) return;
    saveStdinExpanded(ownExpanded);
  }, [controlled, ownExpanded, hydrated]);

  const atLimit = value.length >= STDIN_MAX_LENGTH;
  const showCounter = value.length >= COUNTER_VISIBLE_FROM;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[#0B0B0C]">
      <div className="shrink-0 px-2 pt-1 sm:px-3 sm:pt-2">
        <button
          type="button"
          id="playground-stdin-toggle"
          aria-expanded={expanded}
          aria-controls={expanded ? "playground-stdin" : undefined}
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full items-center gap-2 rounded-lg border border-transparent px-1 py-1.5 text-left text-sm font-medium text-white/80 transition hover:border-white/10 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/40"
        >
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 text-white/50 transition-transform", !expanded && "-rotate-90")}
            aria-hidden
          />
          <span>{t("stdinLabel")}</span>
        </button>
      </div>
      {expanded ? (
        <div className="min-h-0 flex-1 px-2 pb-2 pt-0 sm:px-3 sm:pb-2">
          <label htmlFor="playground-stdin" className="sr-only">
            {t("stdinLabel")}
          </label>
          <textarea
            id="playground-stdin"
            value={value}
            maxLength={STDIN_MAX_LENGTH}
            onChange={(e) => onChange(e.target.value.slice(0, STDIN_MAX_LENGTH))}
            placeholder={t("stdinPlaceholder")}
            aria-describedby={showCounter ? "playground-stdin-count" : undefined}
            style={{ fontSize: `${fontSize}px` }}
            className={cn(
              "h-full min-h-[100px] w-full max-w-full resize-none rounded-lg border border-white/10 bg-[#141416] px-3 py-2 font-mono text-white/90 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E94D1D]/40 lg:min-h-[72px]",
              fontSize < 16 && "playground-console-input-mobile-safe",
            )}
          />
          {showCounter ? (
            <p
              id="playground-stdin-count"
              aria-live="polite"
              className={cn(
                "mt-1 text-right text-[11px] tabular-nums",
                atLimit ? "text-[#FFAB8F]" : "text-white/45",
              )}
            >
              {atLimit
                ? t("stdinLimitReached", { max: STDIN_MAX_LENGTH.toLocaleString() })
                : `${value.length.toLocaleString()} / ${STDIN_MAX_LENGTH.toLocaleString()}`}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
